import { Component } from '@angular/core';

import { DateUtility } from '../../../classes/dateUtility';
import {
	FlowmeterAssignViewMode,
	ICalendarDay,
	IFlowmeterAssignPlanting,
	IFlowmeterEventAssignment,
	IFlowmeterIrrigationEvent,
	IStoredFlowmeterAssignment,
	IWeekEventRow
} from './interfaces';

const ASSIGNMENT_STORAGE = 'cm-flowmeter-assign-proto-v3';
const WEEKDAYS = ['M', 'T', 'W', 'R', 'F', 'S', 'S'];
const EVENT_OFFSETS = [
	-78, -72, -65,
	-52, -45, -38,
	-28, -20, -14, -10, -6, -2, 0
];

@Component({
	selector: 'flowmeter-assign-proto',
	templateUrl: './flowmeter-assign-proto.component.html',
	styleUrls: ['./flowmeter-assign-proto.component.scss']
})
export class FlowmeterAssignProtoComponent {
	plantings: IFlowmeterAssignPlanting[] = [
		{ name: 'Pryor East Romaine', color: '#4e79a7' },
		{ name: 'Pryor West Iceberg', color: '#f28e2b' },
		{ name: 'Lot 12 Strawberries', color: '#59a14f' },
		{ name: 'Block 3 Celery', color: '#e15759' },
		{ name: 'Home Ranch Spinach', color: '#b07aa1' }
	];
	weekdays = WEEKDAYS;
	mode: FlowmeterAssignViewMode = 'week';
	current = 0;
	events: IFlowmeterIrrigationEvent[] = [];
	assignments: IFlowmeterEventAssignment[] = [];
	wetDate: Date;
	harvestDate: Date;
	today: Date;
	view: Date;
	weekStart: Date;
	minView: Date;
	maxView: Date;
	minWeek: Date;
	maxWeek: Date;
	weeks: ICalendarDay[][] = [];
	weekCells: ICalendarDay[] = [];
	weekRows: IWeekEventRow[] = [];
	title = '';
	status = '';

	constructor() {
		this.today = this.noon(new Date());
		this.wetDate = this.addDays(this.today, -70);
		this.harvestDate = this.addDays(this.today, 60);
		this.events = this.buildEvents();
		this.assignments = this.read();
		this.seedClaims();
		this.minView = this.monthStart(this.minDate(this.wetDate, this.firstEvent()));
		this.maxView = this.monthStart(this.minDate(this.harvestDate, this.today));
		this.minWeek = this.monday(this.minDate(this.wetDate, this.firstEvent()));
		this.maxWeek = this.monday(this.today);
		this.view = this.monthStart(this.today);
		this.weekStart = this.monday(this.today);
		this.clampMonth();
		this.buildWeek();
	}

	get planting(): IFlowmeterAssignPlanting {
		return this.plantings[this.current];
	}

	get canGoPrev(): boolean {
		return this.mode === 'month'
			? this.view > this.minView
			: this.weekStart > this.minWeek;
	}

	get canGoNext(): boolean {
		return this.mode === 'month'
			? this.view < this.maxView
			: this.weekStart < this.maxWeek;
	}

	setMode(mode: FlowmeterAssignViewMode): void {
		if (mode === this.mode) {
			return;
		}
		if (mode === 'week') {
			this.weekStart = this.weekFromMonth();
			this.mode = 'week';
			this.buildWeek();
			return;
		}
		this.view = this.monthStart(this.weekStart);
		this.clampMonth();
		this.mode = 'month';
		this.buildMonth();
	}

	prev(): void {
		if (!this.canGoPrev) {
			return;
		}
		if (this.mode === 'month') {
			this.view = new Date(this.view.getFullYear(), this.view.getMonth() - 1, 1);
			this.buildMonth();
			return;
		}
		this.weekStart = this.addDays(this.weekStart, -7);
		this.buildWeek();
	}

	next(): void {
		if (!this.canGoNext) {
			return;
		}
		if (this.mode === 'month') {
			this.view = new Date(this.view.getFullYear(), this.view.getMonth() + 1, 1);
			this.buildMonth();
			return;
		}
		this.weekStart = this.addDays(this.weekStart, 7);
		this.buildWeek();
	}

	onPlantingChange(): void {
		this.mode === 'month' ? this.buildMonth() : this.buildWeek();
	}

	clickDay(cell: ICalendarDay): void {
		if (!cell.hasEvent) {
			return;
		}
		this.ensure(cell.eventDate);
		const assignment = this.findAssignment(cell.eventDate);
		assignment.claimedByPlanting[this.current] = !assignment.claimedByPlanting[this.current];
		this.mode === 'month' ? this.buildMonth() : this.buildWeek();
	}

	save(): void {
		const stored: IStoredFlowmeterAssignment[] = this.assignments.map(assignment => ({
			year: assignment.eventDate.getFullYear(),
			month: assignment.eventDate.getMonth() + 1,
			day: assignment.eventDate.getDate(),
			claimedByPlanting: assignment.claimedByPlanting
		}));
		localStorage.setItem(ASSIGNMENT_STORAGE, JSON.stringify(stored));
		this.status = 'Saved.';
	}

	private weekFromMonth(): Date {
		const sameMonth = this.view.getMonth() === this.today.getMonth()
			&& this.view.getFullYear() === this.today.getFullYear();
		if (sameMonth) {
			return this.clampWeek(this.monday(this.today));
		}
		const year = this.view.getFullYear();
		const month = this.view.getMonth();
		const eventInMonth = this.events.find(event =>
			event.eventDate.getMonth() === month
			&& event.eventDate.getFullYear() === year
			&& event.eventDate <= this.today
		);
		if (eventInMonth) {
			return this.clampWeek(this.monday(eventInMonth.eventDate));
		}
		return this.clampWeek(this.monday(new Date(year, month, 1)));
	}

	private clampWeek(d: Date): Date {
		if (d < this.minWeek) {
			return new Date(this.minWeek);
		}
		if (d > this.maxWeek) {
			return new Date(this.maxWeek);
		}
		return d;
	}

	private clampMonth(): void {
		if (this.view < this.minView) {
			this.view = new Date(this.minView);
		}
		if (this.view > this.maxView) {
			this.view = new Date(this.maxView);
		}
	}

	private buildMonth(): void {
		this.title = this.view.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
		const first = new Date(this.view.getFullYear(), this.view.getMonth(), 1);
		const start = this.addDays(first, -((first.getDay() + 6) % 7));
		this.weeks = [];
		const cur = new Date(start);
		for (let w = 0; w < 6; w++) {
			const week: ICalendarDay[] = [];
			for (let d = 0; d < 7; d++) {
				week.push(this.cell(cur, this.view.getMonth()));
				cur.setDate(cur.getDate() + 1);
			}
			this.weeks.push(week);
		}
	}

	private buildWeek(): void {
		const sunday = this.addDays(this.weekStart, 6);
		this.title = this.short(this.weekStart) + ' – ' + this.short(sunday);
		this.weekCells = [];
		this.weekRows = [];
		for (let i = 0; i < 7; i++) {
			const d = this.addDays(this.weekStart, i);
			const cell = this.cell(d, null);
			this.weekCells.push(cell);
			if (cell.hasEvent) {
				this.weekRows.push(this.eventRow(d, cell));
			}
		}
	}

	private cell(d: Date, month: number | null): ICalendarDay {
		const eventDate = this.noon(d);
		const inMonth = month === null ? true : d.getMonth() === month;
		const event = this.findEvent(eventDate);
		const hasEvent = inMonth && !!event && eventDate <= this.today;
		if (hasEvent) {
			this.ensure(eventDate);
		}
		const assignment = this.findAssignment(eventDate);
		const claimedByPlanting = assignment ? assignment.claimedByPlanting : this.emptyClaims();
		return {
			eventDate,
			day: d.getDate(),
			inMonth,
			hasEvent,
			mine: hasEvent && claimedByPlanting[this.current],
			claimedBy: hasEvent ? this.claimedNames(claimedByPlanting) : []
		};
	}

	private eventRow(d: Date, cell: ICalendarDay): IWeekEventRow {
		const event = this.findEvent(d);
		return {
			eventDate: cell.eventDate,
			dateLabel: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
			gallons: event.gallons,
			minutes: event.minutes,
			applied: event.appliedInches.toFixed(2),
			appRate: event.applicationRate.toFixed(2),
			mine: cell.mine,
			claimedBy: cell.claimedBy
		};
	}

	private ensure(eventDate: Date): void {
		if (this.findAssignment(eventDate)) {
			return;
		}
		const claimedByPlanting = this.emptyClaims();
		claimedByPlanting[this.current] = true;
		this.assignments.push({
			eventDate: this.noon(eventDate),
			claimedByPlanting
		});
	}

	private claimedNames(claimedByPlanting: boolean[]): string[] {
		return this.plantings
			.filter((p, i) => claimedByPlanting[i] && i !== this.current)
			.map(p => p.name);
	}

	private seedClaims(): void {
		if (this.assignments.length) {
			return;
		}
		const eventDate = this.addDays(this.today, -6);
		if (this.findEvent(eventDate)) {
			const claimedByPlanting = this.emptyClaims();
			claimedByPlanting[1] = true;
			this.assignments.push({ eventDate, claimedByPlanting });
		}
	}

	private buildEvents(): IFlowmeterIrrigationEvent[] {
		return EVENT_OFFSETS.map(offset => {
			const eventDate = this.addDays(this.today, offset);
			const n = eventDate.getDate() + eventDate.getMonth() * 13;
			const gallons = 900 + (n * 47) % 2200;
			const minutes = 45 + (n * 11) % 150;
			const appliedInches = (gallons * 0.0000368265993) / 8;
			return {
				eventDate,
				gallons,
				minutes,
				applicationRate: appliedInches / (minutes / 60),
				appliedInches
			};
		});
	}

	private firstEvent(): Date {
		return this.events[0].eventDate;
	}

	private findEvent(eventDate: Date): IFlowmeterIrrigationEvent {
		return this.events.find(event => DateUtility.areEqualDates(event.eventDate, eventDate));
	}

	private findAssignment(eventDate: Date): IFlowmeterEventAssignment {
		return this.assignments.find(assignment =>
			DateUtility.areEqualDates(assignment.eventDate, eventDate)
		);
	}

	private emptyClaims(): boolean[] {
		return this.plantings.map(() => false);
	}

	private monthStart(d: Date): Date {
		return new Date(d.getFullYear(), d.getMonth(), 1);
	}

	private monday(d: Date): Date {
		const m = this.noon(d);
		m.setDate(m.getDate() - ((m.getDay() + 6) % 7));
		return m;
	}

	private minDate(a: Date, b: Date): Date {
		return a < b ? a : b;
	}

	private addDays(d: Date, n: number): Date {
		const x = this.noon(d);
		x.setDate(x.getDate() + n);
		return x;
	}

	private noon(d: Date): Date {
		const x = new Date(d);
		x.setHours(12, 0, 0, 0);
		return x;
	}

	private short(d: Date): string {
		return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
	}

	private read(): IFlowmeterEventAssignment[] {
		try {
			const raw = JSON.parse(localStorage.getItem(ASSIGNMENT_STORAGE) || '[]');
			if (!Array.isArray(raw)) {
				return [];
			}
			return raw.map((item: IStoredFlowmeterAssignment) => ({
				eventDate: this.noon(new Date(item.year, item.month - 1, item.day)),
				claimedByPlanting: item.claimedByPlanting
			}));
		} catch {
			return [];
		}
	}
}
